import { useEffect, useState } from "react";
import { Pencil, Lock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { BrandButton } from "@/components/PageHeader";
import { Field, Pick } from "@/components/event-setup/BasicsSection";
import { api, cedi, errMsg, balanceOf, overpaymentOf } from "@/lib/api";
import { fixedCategoriesFor } from "@/lib/categories";
import { SdGuestsInput } from "@/components/registration/SdGuestsInput";

const NONE = "__none";
const token = (kind, id) => JSON.stringify([kind, id]);
const special = (kind) => ["protocol", "sponsor"].includes(kind);
const initial = (r) => ({
  category_id: special(r.kind) ? r.kind : r.category, protocol_id: r.kind === "protocol" ? r.category : null,
  souvenir_dinner_id: special(r.kind) ? null : r.souvenir_dinner?.id || null,
  attendance: r.attendance || "In-Person", hotel_id: r.accommodation?.hotel_id || null,
  room_id: r.accommodation?.room_id || null, nights: r.accommodation?.nights || 0,
  expected_version: r.registration_version || 0,
  sd_guests: (r.sd_guests || []).map((g) => g.ticket_no),
});

const categoriesFor = (e, r, standing, staff) => {
  const list = fixedCategoriesFor(e, standing, staff).map((c) => ({ ...c, kind: "regular" }));
  if (e.protocol?.enabled) list.push(...e.protocol.subcategories.map((c) => ({ ...c, name: `Protocol – ${c.name}`, kind: "protocol" })));
  if (e.sponsor_category?.enabled) list.push({ id: "sponsor", name: "Sponsor", fee: 0, kind: "sponsor" });
  const kind = special(r.kind) ? r.kind : "regular";
  if (!list.some((c) => c.kind === kind && c.id === r.category)) list.push({ id: r.category, kind, name: `${r.category_name} (current)`, fee: r.category_fee ?? (r.amount - (r.souvenir_dinner?.fee || 0) - (r.accommodation?.amount || 0)) });
  return list.map((c) => ({ ...c, value: token(c.kind, c.id), label: `${c.name} · ${cedi(c.fee)}` }));
};

const hotelsFor = (e, r) => {
  const old = r.accommodation;
  const hotels = (e.hotels || []).filter((h) => (!h.hidden && e.accommodation_mode === "booking") || h.id === old?.hotel_id).map((h) => ({
    ...h, rooms: (h.rooms || []).filter((room) => (h.id === old?.hotel_id && room.id === old.room_id) || (!room.hidden && e.accommodation_mode === "booking" && room.remaining > 0)),
  }));
  if (old) {
    let hotel = hotels.find((h) => h.id === old.hotel_id);
    if (!hotel) { hotel = { id: old.hotel_id, name: old.hotel_name, rooms: [] }; hotels.push(hotel); }
    if (!hotel.rooms.some((room) => room.id === old.room_id)) hotel.rooms.push({ id: old.room_id, name: old.room_name, rate: old.rate, remaining: 0 });
  }
  return hotels.filter((h) => h.rooms.length);
};

const BillingPreview = ({ total, paid, member }) => {
  const bill = { amount: total, paid };
  return <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="edit-reg-bill-preview">
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      <div>Revised bill<strong className="block font-mono" data-testid="edit-reg-total">{cedi(total)}</strong></div>
      <div>Recorded payments<strong className="block font-mono" data-testid="edit-reg-paid">{cedi(paid)}</strong></div>
      <div>{overpaymentOf(bill) > 0 ? "Overpayment" : "Balance due"}<strong className="block font-mono" data-testid="edit-reg-balance">{cedi(overpaymentOf(bill) || balanceOf(bill))}</strong></div>
    </div>
    <p className="text-xs text-amber-900 mt-3">{member ? "Your new total is worked out using the event's current fees. You pay at the event. Once a payment is recorded, only the GAMLS administrators can change this registration." : "Saving recalculates the bill using the latest fees. No member account or event assignment is changed."}</p>
  </div>;
};

const EditForm = ({ data, busy, setBusy, onClose, onSaved, member }) => {
  const { registration: r, event: e, standing } = data;
  const [f, setF] = useState(() => initial(r));
  const set = (patch) => setF((s) => ({ ...s, ...patch }));
  const kind = special(f.category_id) ? f.category_id : "regular";
  const cats = categoriesFor(e, r, standing, !member);
  const catValue = token(kind, kind === "protocol" ? f.protocol_id : f.category_id);
  const cat = cats.find((c) => c.value === catValue);
  const options = e.souvenir_dinner?.enabled ? [...e.souvenir_dinner.options] : [];
  if (!special(r.kind) && r.souvenir_dinner && !options.some((o) => o.id === r.souvenir_dinner.id)) options.push(r.souvenir_dinner);
  const sd = special(kind) ? null : options.find((o) => o.id === f.souvenir_dinner_id);
  const hotels = hotelsFor(e, r);
  const hotel = hotels.find((h) => h.id === f.hotel_id);
  const room = hotel?.rooms.find((v) => v.id === f.room_id);
  const total = (cat?.fee || 0) + (sd?.fee || 0) + (room && f.attendance !== "Online" ? (room.rate || 0) * f.nights : 0);
  const [reason, setReason] = useState("");
  const invalid = !cat || (!member && reason.trim().length < 3) || (f.hotel_id && (!room || !Number.isFinite(room.rate) || !Number.isInteger(f.nights) || f.nights < 1 || f.nights > 30));
  const chooseCategory = (value) => {
    const [nextKind, id] = JSON.parse(value);
    set({ category_id: special(nextKind) ? nextKind : id, protocol_id: nextKind === "protocol" ? id : null,
      souvenir_dinner_id: special(nextKind) || special(kind) ? null : f.souvenir_dinner_id });
  };
  const chooseHotel = (id) => {
    const h = hotels.find((v) => v.id === id);
    set({ hotel_id: h?.id || null, room_id: h?.rooms[0]?.id || null, nights: h ? Math.max(f.nights, 1) : 0 });
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(member ? `/members/me/registrations/${r.id}` : `/transactions/${r.id}/registration`, member ? f : { ...f, reason: reason.trim() });
      toast.success(member ? "Your registration has been updated." : "Registration updated and bill recalculated.");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return <div className="space-y-4">
    <fieldset disabled={busy} className="grid sm:grid-cols-2 gap-4 disabled:opacity-60">
      <Field label="Registration category" className="sm:col-span-2"><Pick value={catValue} onChange={chooseCategory} options={cats} testId="edit-reg-category" /></Field>
      <Field label="Attendance"><Pick value={f.attendance} onChange={(attendance) => set({ attendance, ...(attendance === "Online" ? { hotel_id: null, room_id: null, nights: 0 } : {}) })} options={["In-Person", ...(e.online_attendance || r.attendance === "Online" ? ["Online"] : [])]} testId="edit-reg-attendance" /></Field>
      {special(kind) ? <p className="text-sm text-gray-600 self-end py-2" data-testid="edit-reg-included">Souvenir & Dinner included free</p> : r.souvenir_dinner?.added_by ? <p className="text-sm text-amber-800 self-end py-2" data-testid="edit-reg-sd-added">Souvenir & Dinner: {r.souvenir_dinner.name}</p> : <Field label="Souvenir & Dinner"><Pick value={f.souvenir_dinner_id || NONE} onChange={(v) => set({ souvenir_dinner_id: v === NONE ? null : v, sd_guests: [] })} options={[{ value: NONE, label: "None" }, ...options.map((o) => ({ value: o.id, label: `${o.name} · ${cedi(o.fee)}` }))]} testId="edit-reg-souvenir" />
        <SdGuestsInput option={sd} value={f.sd_guests} onChange={(sd_guests) => set({ sd_guests })} testId="edit-reg-sd-guests" /></Field>}
      {f.attendance !== "Online" && <>
        <Field label="Hotel" className="sm:col-span-2"><Pick value={f.hotel_id || NONE} onChange={chooseHotel} options={[{ value: NONE, label: "No accommodation" }, ...hotels.map((h) => ({ value: h.id, label: h.name }))]} testId="edit-reg-hotel" /></Field>
        {f.hotel_id && <>
          <Field label="Room type"><Pick value={f.room_id || ""} onChange={(room_id) => set({ room_id })} options={(hotel?.rooms || []).map((v) => ({ value: v.id, label: `${v.name} · ${cedi(v.rate)}/night${r.accommodation?.room_id === v.id && r.accommodation?.hotel_id === hotel.id ? " (current booking)" : ` · ${v.remaining} left`}` }))} testId="edit-reg-room" /></Field>
          <Field label="Nights"><Input type="number" min="1" max="30" step="1" value={f.nights || ""} onChange={(v) => set({ nights: Number(v.target.value) })} data-testid="edit-reg-nights" /></Field>
        </>}
      </>}
    </fieldset>
    <BillingPreview total={total} paid={r.paid || 0} member={member} />
    {!member && <Field label="Reason for this change (kept in the history)"><Input value={reason} onChange={(v) => setReason(v.target.value)} maxLength={300} placeholder="e.g. Participant asked to switch to Online attendance" data-testid="edit-reg-reason" /></Field>}
    <div className="flex flex-col sm:flex-row justify-end gap-2 border-t pt-4">
      <BrandButton variant="outline" onClick={onClose} disabled={busy} data-testid="edit-reg-cancel">Cancel</BrandButton>
      <BrandButton onClick={save} disabled={busy || invalid} data-testid="edit-reg-save">{busy ? "Saving…" : "Save registration"}</BrandButton>
    </div>
  </div>;
};

export const EditRegistrationButton = ({ r, onChanged, member = false }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const paid = !member && ((r.payments || []).length > 0 || (r.paid || 0) > 0);
  useEffect(() => {
    let active = true;
    if (open) {
      setData(null); setError("");
      api.get(member ? `/members/me/registrations/${r.id}/edit-options` : `/transactions/${r.id}/edit-options`).then((v) => { if (active) setData(v.data); }).catch((err) => { if (active) setError(errMsg(err)); });
    }
    return () => { active = false; };
  }, [open, r.id, member]);
  if (paid) return <span className="text-[11px] text-gray-500 inline-flex items-center gap-1" title="A payment receipt is recorded. Void the receipt(s) in Payments & Receipts before editing." data-testid={`evreg-edit-locked-${r.ticket_no}`}><Lock className="w-3 h-3" />Void receipt to edit</span>;
  return <>
    <button onClick={() => setOpen(true)} className="text-xs font-semibold text-[#B30000] hover:underline inline-flex items-center gap-1" data-testid={`${member ? "my" : "evreg"}-edit-${r.ticket_no}`}><Pencil className="w-3.5 h-3.5" />Edit</button>
    <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg" data-testid="edit-reg-dialog">
        <DialogHeader>
          <DialogTitle data-testid="edit-reg-title">Edit event registration</DialogTitle>
          <DialogDescription data-testid="edit-reg-context">{r.delegate_name} · {r.event_title || data?.event.title} · {r.ticket_no}</DialogDescription>
        </DialogHeader>
        {error ? <p role="alert" className="text-red-800 text-sm" data-testid="edit-reg-error">{error}. Close this editor and try again.</p>
          : !data ? <p className="text-sm text-gray-500" data-testid="edit-reg-loading">Loading current selections and room availability…</p>
            : <EditForm data={data} member={member} busy={busy} setBusy={setBusy} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); onChanged(); }} />}
      </DialogContent>
    </Dialog>
  </>;
};
